import * as yup from 'yup';
import { t } from 'i18next';
import {
  addOptionalToSchema,
  pastOrTodayDateSchema,
  requiredErrorMessage,
  textSchema,
} from '@common/schema';
import { components, ProgramName } from '@common/types';

// Human-readable field name used in validation messages (e.g. yup's default
// "${path} must be one of ..." resolves ${path} to this label).
const fieldLabel = (key: string) =>
  t(`patient.detailsCard.editPatientProgram.consent.fields.${key}`);

type ConsentRequest =
  components['schemas']['AddEditProgramEnrollmentConsentRequest'];
type ConsentResponseEntry =
  components['schemas']['ProgramEnrollmentConsentResponse'];

export type ConsentType = ConsentRequest['consent_type'];
export type ConsentHowObtained = ConsentRequest['how_obtained'];
export type ConsentStatus = ConsentRequest['status'];
export type ConsentObtainedBy = ConsentRequest['obtained_by'];
export type ConsentWhoConsented = ConsentRequest['who_consented'];
export type ConsentExpires = ConsentRequest['expires'];

export type ConsentBody = ConsentRequest;
export type ConsentResponse = ConsentResponseEntry;

export const CONSENT_TYPE_OPTIONS: { value: ConsentType; label: string }[] = [
  { value: 'Universal', label: 'Universal' },
  { value: 'RPM', label: 'RPM' },
  { value: 'GUIDE', label: 'GUIDE' },
  { value: 'Optiversal', label: 'Optiversal' },
];

export const HOW_OBTAINED_OPTIONS: {
  value: ConsentHowObtained;
  label: string;
}[] = [
  { value: 'Written', label: 'Written' },
  { value: 'Verbal', label: 'Verbal' },
];

export const CONSENT_STATUS_OPTIONS: { value: ConsentStatus; label: string }[] =
  [
    { value: 'Declined / Refused', label: 'Declined / Refused' },
    { value: 'Withdrawn', label: 'Withdrawn' },
    { value: 'Missing', label: 'Missing' },
    { value: 'Obtained', label: 'Obtained' },
  ];

export const OBTAINED_BY_OPTIONS: {
  value: ConsentObtainedBy;
  label: string;
}[] = [
  { value: 'CN', label: 'CN' },
  { value: 'Intake', label: 'Intake' },
  { value: 'Provider', label: 'Provider' },
];

export const WHO_CONSENTED_OPTIONS: {
  value: ConsentWhoConsented;
  label: string;
}[] = [
  { value: 'Patient', label: 'Patient' },
  { value: 'Other', label: 'Other' },
];

export const EXPIRES_OPTIONS: { value: ConsentExpires; label: string }[] = [
  { value: 'Never', label: 'Never' },
  { value: '12 months', label: 'In 12 months' },
];

const UNIVERSAL_EXCLUDED_PROGRAMS: string[] = [
  ProgramName.MEMORY_CONNECT,
  ProgramName.CARE_AT_HOME,
  ProgramName.RPM,
  ProgramName.GUIDE,
];

/**
 * Consent-type availability by program:
 * - Universal:  every program except MC, CAH, RPM and GUIDE
 * - RPM:        RPM only
 * - GUIDE:      GUIDE only
 * - Optiversal: MC and CAH (Care at Home) only
 */
export const getConsentTypeOptions = (programName: string) =>
  CONSENT_TYPE_OPTIONS.filter(({ value }) => {
    switch (value) {
      case 'Universal':
        return !UNIVERSAL_EXCLUDED_PROGRAMS.includes(programName);
      case 'RPM':
        return programName === ProgramName.RPM;
      case 'GUIDE':
        return programName === ProgramName.GUIDE;
      case 'Optiversal':
        return (
          programName === ProgramName.MEMORY_CONNECT ||
          programName === ProgramName.CARE_AT_HOME
        );
      default:
        return false;
    }
  });

export const consentEntrySchema = yup.object({
  id: yup.string().optional(),

  consent_type: yup
    .mixed<ConsentType>()
    .label(fieldLabel('consentType'))
    .oneOf(
      CONSENT_TYPE_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),

  how_obtained: yup
    .mixed<ConsentHowObtained>()
    .label(fieldLabel('howObtained'))
    .oneOf(
      HOW_OBTAINED_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),

  date_obtained: pastOrTodayDateSchema
    .label(fieldLabel('dateObtained'))
    .required(requiredErrorMessage),

  status: yup
    .mixed<ConsentStatus>()
    .label(fieldLabel('status'))
    .oneOf(
      CONSENT_STATUS_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),

  obtained_by: yup
    .mixed<ConsentObtainedBy>()
    .label(fieldLabel('obtainedBy'))
    .oneOf(
      OBTAINED_BY_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),

  who_consented: yup
    .mixed<ConsentWhoConsented>()
    .label(fieldLabel('whoConsented'))
    .oneOf(
      WHO_CONSENTED_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),

  // required & present only when who_consented === 'Other'
  other_specify: textSchema
    .label(fieldLabel('otherSpecify'))
    .when('who_consented', ([who_consented], schema) =>
      who_consented === 'Other'
        ? schema.required(requiredErrorMessage)
        : addOptionalToSchema(schema),
    ),

  expires: yup
    .mixed<ConsentExpires>()
    .label(fieldLabel('expires'))
    .oneOf(
      EXPIRES_OPTIONS.map(o => o.value),
      requiredErrorMessage,
    )
    .required(requiredErrorMessage),
});

export const consentFormSchema = yup.object({
  consents: yup.array().of(consentEntrySchema).required(),
});

export type ConsentEntryFormShape = yup.InferType<typeof consentEntrySchema>;
export type ConsentFormShape = yup.InferType<typeof consentFormSchema>;

/**
 * Form-values types used with react-hook-form. Select fields allow an empty
 * string so a brand-new (blank) entry can be represented before the user picks
 * a value; the yup schema enforces the concrete unions on submit.
 */
export interface ConsentEntryValues {
  id?: string;
  consent_type: ConsentType | '';
  how_obtained: ConsentHowObtained | '';
  date_obtained: string;
  status: ConsentStatus | '';
  obtained_by: ConsentObtainedBy | '';
  who_consented: ConsentWhoConsented | '';
  other_specify: string;
  expires: ConsentExpires | '';
}

export interface ConsentFormValues {
  consents: ConsentEntryValues[];
}
